import { useState, useMemo, useCallback } from "react";
import { useEvents } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { MapPin, AlertTriangle, Clock, Navigation, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const typeEmoji: Record<string, string> = {
  missile: "🚀",
  drone: "🛩️",
  air_raid: "🚨",
  explosion: "💥",
  movement: "🪖",
};

const typeColors: Record<string, string> = {
  missile: "hsl(var(--destructive))",
  drone: "hsl(var(--danger))",
  air_raid: "hsl(var(--warning))",
  explosion: "hsl(var(--danger))",
  movement: "hsl(var(--muted-foreground))",
};

function timeAgo(iso: string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.floor(diff)} min ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
}

const ThreatsNearMe = () => {
  const { isPremium } = useAuth();
  const { data: events } = useEvents(undefined, isPremium);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      return;
    }
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLoading(false);
      },
      (err) => {
        console.error("Geolocation error:", err.code, err.message);
        if (err.code === 1) {
          setError("Location access denied. Please allow location access in your browser settings.");
        } else if (err.code === 2) {
          setError("Location unavailable. Try again or check your device settings.");
        } else {
          setError("Location request timed out. Please try again.");
        }
        setLoading(false);
      },
      { timeout: 15000, enableHighAccuracy: false, maximumAge: 300000 }
    );
  }, []);

  const nearbyThreats = useMemo(() => {
    if (!location || !events?.length) return [];
    return events
      .filter((e) => e.lat && e.lng)
      .map((e) => ({
        ...e,
        distance: haversine(location.lat, location.lng, e.lat!, e.lng!),
      }))
      .filter((e) => e.distance <= 500)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 10);
  }, [location, events]);

  const closestThreat = nearbyThreats[0];

  return (
    <section className="border-b border-border">
      <div className="container mx-auto px-4 py-8">
        {!location ? (
          <div className="mx-auto max-w-lg text-center">
            <div className="mb-3 sm:mb-4 inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
              <Navigation className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
            </div>
            <h2 className="mb-2 text-xl sm:text-2xl font-black text-foreground">
              Is it safe near you?
            </h2>
            <p className="mb-4 sm:mb-6 text-xs sm:text-sm text-muted-foreground px-2">
              See active conflict events within 500 km of your location in real time.
            </p>
            <Button
              size="lg"
              onClick={requestLocation}
              disabled={loading}
              className="gap-2 text-base font-bold"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <MapPin className="h-5 w-5" />
              )}
              {loading ? "Detecting location..." : "📍 Show threats near me"}
            </Button>
            {error && (
              <p className="mt-3 text-xs text-destructive">{error}</p>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-4 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" /> Threats Near You
                </h2>
                <p className="text-[10px] sm:text-xs text-muted-foreground font-mono">
                  {nearbyThreats.length} events within 500 km
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={requestLocation} className="text-[10px] sm:text-xs shrink-0 h-8 px-2 sm:px-3">
                <Navigation className="mr-1 h-3 w-3" /> Refresh
              </Button>
            </div>

            {nearbyThreats.length === 0 ? (
              <div className="rounded-lg border border-success/30 bg-success/5 p-6 text-center">
                <p className="text-lg font-bold text-success">✅ No threats detected nearby</p>
                <p className="mt-1 text-sm text-muted-foreground">No conflict events within 500 km of your location</p>
              </div>
            ) : (
              <div className="space-y-2">
                <AnimatePresence>
                  {nearbyThreats.map((threat, i) => (
                    <motion.div
                      key={threat.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className={`flex items-center gap-3 rounded-lg border bg-card p-3 transition-colors ${
                        i === 0 ? "border-destructive/40" : "border-border"
                      }`}
                    >
                      <span className="text-xl">{typeEmoji[threat.event_type] || "⚠️"}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {threat.title}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{threat.city || threat.country || "Unknown"}</span>
                          <span>•</span>
                          <Badge
                            variant="outline"
                            className="font-mono text-[10px] uppercase"
                            style={{ color: typeColors[threat.event_type], borderColor: typeColors[threat.event_type] }}
                          >
                            {threat.event_type.replace("_", " ")}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-sm font-bold text-foreground">
                          {threat.distance.toFixed(0)} km
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {timeAgo(threat.occurred_at)}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ThreatsNearMe;
